import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Calendar, MapPin, Edit3, ArrowRight, Eye } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, query, where, getDocs, doc, setDoc } from 'firebase/firestore';
import { NotificationBell } from '../components/NotificationBell';
import { UserMenu } from '../components/UserMenu';
import { Logo } from '../components/Logo';
import { PayoutConnectCard } from '../components/PayoutConnectCard';
import toast from 'react-hot-toast';

interface PitchFormat {
  id: string;
  width: string;
  depth: string;
  flooring: string;
  price: string;
}

interface Festival {
  id: string;
  name: string;
  category: string;
  location: string;
  startDate: string;
  endDate: string;
  capacity?: number;
  dailyPax?: number;
  terrainType?: string;
  technical?: {
    powerKva: number;
    has16A: boolean;
    has32A: boolean;
    has63A: boolean;
    hasDrinkingWater: boolean;
    wastewaterDetails: string;
    providesTables: boolean;
    pitches: PitchFormat[];
  };
  branding?: {
    description: string;
    coverImage: string;
    logoImage: string;
    gallery: string[];
    highlights?: string[];
  };
  status?: string;
}

export default function MyFestivalsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [festivals, setFestivals] = useState<Festival[]>([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const handleSeedDemoData = async () => {
    if (!user) return;
    setSeeding(true);
    try {
      const fest1Id = "perei-fest-2026";
      const fest1Ref = doc(db, 'festivals', fest1Id);
      await setDoc(fest1Ref, {
        id: fest1Id,
        name: "PÉRIPHÉRIQUE FESTIVAL",
        category: "music",
        location: "Périphérique Nord, Paris, France",
        startDate: "2026-07-10",
        endDate: "2026-07-12",
        status: "published",
        ownerId: user.uid,
        totalStands: 20,
        capacity: 20,
        dailyPax: 15000,
        terrainType: "goudron",
        description: "Le plus grand festival d'électronique brute sous le périphérique de Paris. Une expérience industrielle sans égal.",
        about: "Né de l'amour des cultures industrielles, Périphérique investit les dessous de la ville lumière pour 3 nuits de fête techno et de basses intenses.",
        audienceProfile: "Jeune, 18-35 ans, féru de musique électronique underground, résidents franciliens.",
        editionNumber: 3,
        ticketingUrl: "https://shotgun.live/fr",
        musicStyles: ["electro"],
        musicSubstyles: ["techno", "house", "industrial"],
        technical: {
          powerKva: 120,
          has16A: true,
          has32A: true,
          has63A: true,
          hasDrinkingWater: true,
          wastewaterDetails: "Cuves de récupération d'eaux usées collectives",
          providesTables: true,
          pitches: [
            { id: "p1", width: "3", depth: "3", flooring: "goudron", price: "250" },
            { id: "p2", width: "4", depth: "3", flooring: "goudron", price: "350" }
          ]
        },
        branding: {
          description: "Le plus grand festival d'électronique brute.",
          coverImage: "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&q=80",
          logoImage: "",
          gallery: [],
          highlights: ["Sound System d'exception", "Grand espace Street Food", "Droit de bouchon offert"]
        },
        planElements: [
          { id: "app1", type: "stand", name: "L'Atelier Burger", x: 100, y: 150, w: 80, h: 60 },
          { id: "app3", type: "stand", name: "La Crêpe Dorée", x: 220, y: 150, w: 60, h: 60 }
        ],
        createdAt: new Date().toISOString()
      });

      const fest2Id = "street-food-arena-2026";
      const fest2Ref = doc(db, 'festivals', fest2Id);
      await setDoc(fest2Ref, {
        id: fest2Id,
        name: "STREET FOOD ARENA",
        category: "food",
        location: "Halle Tony Garnier, Lyon, France",
        startDate: "2026-08-14",
        endDate: "2026-08-16",
        status: "published",
        ownerId: user.uid,
        totalStands: 35,
        capacity: 35,
        dailyPax: 22000,
        terrainType: "interieur",
        description: "La grand messe de la cuisine de rue lyonnaise. 3 jours de dégustation, battles de chefs, et ambiance sonore unique.",
        about: "À Lyon, la gastronomie est sacrée. Street Food Arena invite 35 artisans culinaires à faire découvrir le meilleur des saveurs locales et du monde.",
        audienceProfile: "Familles, actifs, fins gourmets, 20-55 ans.",
        editionNumber: 5,
        ticketingUrl: "https://festin.app",
        technical: {
          powerKva: 160,
          has16A: true,
          has32A: true,
          has63A: false,
          hasDrinkingWater: true,
          wastewaterDetails: "Évacuation directe au sol dans chaque zone",
          providesTables: false,
          pitches: [
            { id: "s1", width: "3", depth: "2", flooring: "béton", price: "180" },
            { id: "s2", width: "4", depth: "3", flooring: "béton", price: "280" }
          ]
        },
        branding: {
          description: "La grand messe de la cuisine de rue lyonnaise.",
          coverImage: "https://images.unsplash.com/photo-1565123409695-7b5ef63a2efb?auto=format&fit=crop&q=80",
          logoImage: "",
          gallery: [],
        },
        createdAt: new Date().toISOString()
      });

      const apps = [
        {
          id: "app1",
          festivalId: fest1Id,
          standId: "stand-user-1",
          standName: "L'Atelier Burger",
          applicantName: "Marc Vasseur",
          applicantEmail: "marc@atelierburger.fr",
          applicantId: "stand-user-1",
          category: "food_truck",
          format: "3x3",
          appliedAt: new Date(Date.now() - 4 * 24 * 3600 * 1000).toISOString(),
          status: "pending",
          electricity: "32A",
          water: "true",
          comments: "Nous proposons des burgers gourmets avec frites maison à base de produits 100% bio et locaux. On espère rejoindre l'aventure !"
        },
        {
          id: "app2",
          festivalId: fest1Id,
          standId: "stand-user-2",
          standName: "Fripes & Co",
          applicantName: "Charlotte Dubois",
          applicantEmail: "charlotte@fripesco.com",
          applicantId: "stand-user-2",
          category: "clothes",
          format: "4x3",
          appliedAt: new Date(Date.now() - 2 * 24 * 3600 * 1000).toISOString(),
          status: "approuve_paiement_requis",
          electricity: "16A",
          water: "false",
          comments: "Sélection de vêtements vintage des années 80-90. Stand hyper visuel avec néons orange."
        },
        {
          id: "app3",
          festivalId: fest1Id,
          standId: "stand-user-3",
          standName: "La Crêpe Dorée",
          applicantName: "Jean Le Gal",
          applicantEmail: "jean@crepedoree.fr",
          applicantId: "stand-user-3",
          category: "food_truck",
          format: "3x2",
          appliedAt: new Date(Date.now() - 5 * 24 * 3600 * 1000).toISOString(),
          status: "approved",
          electricity: "16A",
          water: "true",
          comments: "Pancakes et crêpes bretonnes salées/sucrées cuites minute."
        },
        {
          id: "app4",
          festivalId: fest1Id,
          standId: "stand-user-4",
          standName: "Poterie Locale",
          applicantName: "Stéphane Rossi",
          applicantEmail: "stephane@poterie.fr",
          applicantId: "stand-user-4",
          category: "artisan",
          format: "3x3",
          appliedAt: new Date(Date.now() - 6 * 24 * 3600 * 1000).toISOString(),
          status: "rejected",
          electricity: "none",
          water: "false",
          comments: "Potentiels vases et ustensiles en argile cuits dans notre atelier drômois."
        }
      ];

      for (const app of apps) {
        await setDoc(doc(db, 'applications', app.id), app);
      }

      setFestivals([
        { id: fest1Id, name: "PÉRIPHÉRIQUE FESTIVAL", category: "music", location: "Périphérique Nord, Paris, France", startDate: "2026-07-10", endDate: "2026-07-12", status: "published", capacity: 20, dailyPax: 15000, branding: { coverImage: "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&q=80", description: "Le plus grand festival d'électronique brute." } },
        { id: fest2Id, name: "STREET FOOD ARENA", category: "food", location: "Halle Tony Garnier, Lyon, France", startDate: "2026-08-14", endDate: "2026-08-16", status: "published", capacity: 35, dailyPax: 22000, branding: { coverImage: "https://images.unsplash.com/photo-1565123409695-7b5ef63a2efb?auto=format&fit=crop&q=80", description: "La grand messe de la cuisine de rue lyonnaise." } }
      ] as any);

      toast.success("Données de démonstration injectées ! Explorez vos tableaux de bord !");
    } catch (e) {
      console.error(e);
      toast.error("Erreur d'injection des données de démonstration.");
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => {
    const fetchFestivals = async () => {
      if (!user) return;
      try {
        const q = query(collection(db, 'festivals'), where('ownerId', '==', user.uid));
        const querySnapshot = await getDocs(q);
        const fetchedFests: Festival[] = [];
        querySnapshot.forEach((doc) => {
          fetchedFests.push({ id: doc.id, ...doc.data() } as Festival);
        });
        setFestivals(fetchedFests);
      } catch (error) {
        console.error("Error fetching festivals", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFestivals();
  }, [user]);

  return (
    <div className="min-h-screen bg-[#0d0e12] text-white font-sans">
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-black mb-2 tracking-tight">Mes Festivals</h1>
            <p className="text-gray-400">Gérez vos événements et la configuration de vos appels d'offres.</p>
          </div>
          <button 
            onClick={() => navigate('/choose-creation-method', { state: { forceNew: true } })}
            className="group flex flex-col items-center justify-center bg-[#FF5C00] hover:bg-[#ff7a33] text-white w-16 h-16 rounded-2xl transition-all shadow-[0_8px_30px_rgba(255,92,0,0.3)] hover:-translate-y-1 active:scale-95 shrink-0"
            title="Créer un nouveau festival"
          >
            <Plus className="w-8 h-8 group-hover:rotate-90 transition-transform duration-300" />
            <span className="text-[7px] font-black uppercase tracking-[0.2em] text-[#FFC7A8] group-hover:text-white transition-colors mt-0.5">New</span>
          </button>
        </div>

        {/* Stripe Connect : recevoir automatiquement les droits d'entrée */}
        <PayoutConnectCard />

        {loading ? (
          <div className="flex justify-center items-center h-64 border border-white/10 rounded-3xl bg-[#121212]">
            <div className="w-8 h-8 border-4 border-[#FF5C00]/20 border-t-[#FF5C00] rounded-full animate-spin"></div>
          </div>
        ) : festivals.length === 0 ? (
          <div className="text-center py-32 border border-white/10 border-dashed rounded-3xl bg-[#121212] flex flex-col items-center shadow-2xl">
            <div className="w-24 h-24 bg-white/5 rounded-full flex items-center justify-center mb-6">
              <Calendar className="w-12 h-12 text-[#4169E1]" />
            </div>
            <h2 className="text-3xl font-black mb-4">Aucun festival publié</h2>
            <p className="text-gray-400 max-w-md mx-auto mb-8">Lancez votre première édition et trouvez les meilleurs stands pour régaler votre public.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center w-full max-w-lg mt-2 px-4">
              <button 
                onClick={() => navigate('/choose-creation-method')}
                className="w-full sm:w-auto bg-[#FF5C00] hover:bg-[#ff7a33] text-white px-8 py-4 rounded-xl font-bold transition-all shadow-[0_0_20px_rgba(255,92,0,0.4)] hover:-translate-y-1"
              >
                Lancer ma première édition
              </button>
              
              <button 
                onClick={handleSeedDemoData}
                disabled={seeding}
                className="w-full sm:w-auto flex items-center justify-center gap-2 text-[#FF5C00] hover:text-[#ff7a33] bg-[#FF5C00]/10 hover:bg-[#FF5C00]/20 px-8 py-4 rounded-xl font-bold transition-all border border-[#FF5C00]/30 hover:border-[#FF5C00]/50"
              >
                {seeding ? (
                  <>
                    <div className="w-4 h-4 border-2 border-[#FF5C00]/20 border-t-[#FF5C00] rounded-full animate-spin"></div>
                    Injection...
                  </>
                ) : (
                  "⚡️ Générer des données de démo"
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {festivals.map(fest => (
              <div 
                key={fest.id} 
                className="group cursor-pointer flex flex-col bg-[#121212] rounded-2xl overflow-hidden border border-white/10 hover:border-[#FF5C00]/50 transition-all shadow-xl hover:-translate-y-1"
                onClick={() => navigate(`/manage-festival/${fest.id}`)}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-[#121212]">
                  <img 
                    src={fest.branding?.coverImage || "https://images.unsplash.com/photo-1540039155733-d7696d4ebc59?auto=format&fit=crop&q=80"} 
                    alt={fest.name} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                  
                  <div className="absolute top-3 left-3 flex gap-2">
                    <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider backdrop-blur-md border ${
                      fest.status === 'published' ? 'bg-green-500/20 text-green-500 border-green-500/30' :
                      fest.status === 'pending_review' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                      fest.status === 'docs_rejected' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                      'bg-orange-500/20 text-orange-500 border-orange-500/30'
                    }`}>
                      {fest.status === 'published' ? 'Publié' :
                       fest.status === 'pending_review' ? 'En validation' :
                       fest.status === 'docs_rejected' ? 'Docs refusés' :
                       'Brouillon'}
                    </span>
                  </div>
                </div>
                
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-lg leading-tight text-white line-clamp-2">{fest.name}</h3>
                  </div>
                  
                  <div className="mt-auto space-y-2 pt-4">
                    <div className="flex items-center text-xs text-gray-400">
                      <Calendar className="w-3.5 h-3.5 mr-1.5 shrink-0" />
                      <span className="truncate">{fest.startDate || '-'}</span>
                    </div>
                    <div className="flex items-center text-xs text-gray-400">
                      <MapPin className="w-3.5 h-3.5 mr-1.5 shrink-0" />
                      <span className="truncate">{fest.location || '-'}</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-white/10 flex justify-between items-center">
                     <div className="flex items-center space-x-3">
                        <span className="text-xs text-gray-500 font-semibold">{fest.capacity || 0} stands</span>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/festival/${fest.id}`);
                          }}
                          className="p-1.5 bg-white/5 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-all"
                          title="Voir la page publique"
                        >
                          <Eye className="w-4 h-4" /> 
                        </button>
                     </div>
                     <span className="text-xs text-[#FF5C00] font-bold flex items-center group-hover:underline">
                        Gérer <ArrowRight className="w-3 h-3 ml-1" />
                     </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}


